import { Button, Flex, Text } from '@components';
import { ResetPassword as ResetPasswordForm } from '@features/api/request/ResetPassword';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuth } from '@hooks/useAuth';
import { useTheme, Box } from '@mui/material';
import { BackButton } from '@pages/SignUp/SignUp.styles';
import { registerInput } from '@utils/reactHookForm';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { resetPasswordRequestFormSchema } from '@validationSchemas/resetPassword';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { StyledResetEmailInput } from './ResetPassword.styles';

const ResetPassword: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const intl = useIntl();
  const { sendPasswordResetRequest } = useAuth();
  const {
    register,
    handleSubmit,
    watch,
    trigger,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<ResetPasswordForm>({
    resolver: yupResolver(resetPasswordRequestFormSchema),
    mode: 'onSubmit',
    shouldFocusError: false,
  });

  const email = watch('email');

  useEffect(() => {
    const emailAddress = getValues('email');
    if (emailAddress && emailAddress?.indexOf(' ') > -1) {
      setValue('email', emailAddress?.trim());
      trigger('email');
    }
  }, [email, trigger, setValue, getValues]);

  const handleBackClick = () => navigate(-1);

  const onSubmit = async (data: ResetPasswordForm) => {
    try {
      await sendPasswordResetRequest(data);
      navigate('/recovery-email-sent');
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Flex
      flex={1}
      flexDirection='column'
      sx={{
        maxWidth: 550,
        width: '100%',
        flex: 1,
        justifyContent: 'center',
        alignSelf: 'center',
      }}
    >
      <Box mb={10}>
        <BackButton
          variant='tertiary'
          color='primary'
          data-testid='reset-password-back-button'
          onClick={handleBackClick}
        >
          <FormattedMessage id='misc.back' />
        </BackButton>
      </Box>
      <Text variant='h4' fontWeight='fontWeightBold'>
        <FormattedMessage id='resetPassword.resetYourPassword' />
      </Text>
      <Text
        variant='h5'
        fontWeight='fontWeightRegular'
        style={{
          color: theme.palette.grey.A100,
          marginTop: theme.spacing(3),
        }}
      >
        <FormattedMessage id='resetPassword.justLetUsKnow' />
      </Text>
      <form onSubmit={handleSubmit(onSubmit)} style={{ marginTop: 16 }}>
        <StyledResetEmailInput
          label={intl.formatMessage({
            id: 'misc.emailAddress',
          })}
          autoFocus
          fullWidth
          inputProps={{
            'data-testid': 'reset-email-address-input',
          }}
          autoComplete='email'
          {...registerInput(register, 'email')}
          {...getErrorProps(intl, errors.email)}
        />
        <Box mt={16}>
          <Button
            data-testid='send-password-reset-button'
            fullWidth
            variant='primary'
            size='large'
            type='submit'
            color='primary'
          >
            <FormattedMessage id='resetPassword.sendPasswordResetEmail' />
          </Button>
        </Box>
      </form>
    </Flex>
  );
};

export default ResetPassword;
